import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function EmailConfirm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('Confirming your email...');

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        // Supabase automatically handles email confirmation via hash fragment
        // when user lands on this page with the confirmation link
        // The onAuthStateChange listener in AuthContext will detect SIGNED_IN event
        
        // Check if we have a hash fragment (Supabase tokens)
        const hashParams = new URLSearchParams(window?.location?.hash?.substring(1));
        const accessToken = hashParams?.get('access_token');
        const type = hashParams?.get('type');
        
        if (!accessToken || type !== 'signup') {
          // Check if user is already authenticated (confirmation already happened)
          const { data: { session } } = await supabase?.auth?.getSession();
          
          if (session?.user) {
            setStatus('success');
            setMessage('Email already confirmed! Redirecting to sign in...');
            setTimeout(() => {
              navigate('/signin', { 
                state: { 
                  message: 'Email confirmed successfully! You can now sign in.',
                  email: session?.user?.email 
                } 
              });
            }, 2000);
            return;
          }
          
          setStatus('error');
          setMessage('Invalid or expired confirmation link. Please check your email or request a new confirmation.');
          return;
        }

        // Set up a listener for auth state changes
        const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
          console.log('Email confirmation - Auth event:', event);

          if (event === 'SIGNED_IN' && session?.user) {
            setStatus('success');
            setMessage('Email confirmed successfully! Redirecting to sign in...');
            
            // Clean up subscription
            subscription?.unsubscribe();
            
            // Sign out the user so they can properly sign in
            await supabase?.auth?.signOut();
            
            // Redirect after a short delay
            setTimeout(() => {
              navigate('/signin', { 
                state: { 
                  message: 'Email confirmed successfully! You can now sign in.',
                  email: session?.user?.email 
                } 
              });
            }, 2000);
          } else if (event === 'TOKEN_REFRESHED') {
            // Token refresh is normal, continue waiting
            console.log('Token refreshed during confirmation');
          } else if (event === 'SIGNED_OUT') {
            // If signed out during confirmation, might indicate error
            console.log('Signed out during confirmation');
          }
        });

        // Wait for Supabase to process the tokens from URL
        // The onAuthStateChange will handle the success case
        
        // Set a timeout in case the confirmation doesn't happen
        const timeout = setTimeout(() => {
          supabase?.auth?.getSession()?.then(({ data: { session } }) => {
            if (!session) {
              setStatus('error');
              setMessage('Confirmation timeout. Please try clicking the link again or request a new confirmation email.');
              subscription?.unsubscribe();
            }
          });
        }, 10000); // 10 second timeout

        // Clean up on unmount
        return () => {
          clearTimeout(timeout);
          subscription?.unsubscribe();
        };

      } catch (err) {
        console.error('Unexpected error during email confirmation:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again or contact support.');
      }
    };

    confirmEmail();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          {/* Status Icon */}
          <div className="mx-auto w-20 h-20 mb-6 flex items-center justify-center">
            {status === 'loading' && (
              <Loader2 className="w-20 h-20 text-green-600 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="w-20 h-20 text-green-600" />
            )}
            {status === 'error' && (
              <XCircle className="w-20 h-20 text-red-600" />
            )}
          </div>

          {/* Status Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {status === 'loading' && 'Confirming Email'}
            {status === 'success' && 'Email Confirmed!'}
            {status === 'error' && 'Confirmation Failed'}
          </h1>

          {/* Status Message */}
          <p className="text-gray-600 mb-6">
            {message}
          </p>

          {/* Action Buttons */}
          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/signin')}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Go to Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Sign Up Again
              </button>
            </div>
          )}

          {status === 'success' && (
            <div className="text-sm text-gray-500">
              <p>You will be redirected automatically...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}